import React, { useMemo } from 'react';

import { Formik } from 'formik';

import { services, metadata, unstable } from '@broadleaf/admin-components';

import classNames from 'classnames';
import {
  isUpdate,
  useCurrentForm,
  useHandleSubmit,
  useContextParams,
  useEntityState,
  entityViewDefaultReducer,
  entityViewReducerDefaultState
} from 'app/common';
import { AuroraTextField } from 'app/common/components';

const { Card, useHasAccess, MetadataUtils } = unstable;
const EntityView = metadata.views.EntityView;

const { findEndpoint, getEndpointByType } = MetadataUtils;
const { SchemaService } = services;

/**
 * Basic form to add to a browse entity grid. Basic implementation without
 * any sandbox support. Only tested with application discrimination.
 * @param props
 * @return {Element}
 * @constructor
 */
const GridEntityForm = props => {
  const { metadata, components: componentsProp } = props;
  const { className } = metadata;
  const readOnly = useIsReadOnly(metadata);

  const components = useMemo(
    () => defaultComponents(componentsProp),
    [componentsProp]
  );
  const readConfig = useMemo(() => {
    return getEndpointByType(metadata, 'READ');
  }, [metadata]);
  const contextParams = useContextParams(metadata);

  const [state, dispatch] = useEntityState({
    config: readConfig,
    context: contextParams,
    entityViewReducerDefaultState,
    entityViewDefaultReducer
  });

  const formikProps = useFormikProps({
    contextParams,
    dispatch,
    metadata,
    state
  });
  return (
    <div className={classNames('GridEntityForm', className)}>
      <Card>
        <Formik {...formikProps}>
          {formik => {
            return (
              <components.Entity
                components={components}
                dispatch={dispatch}
                formik={formik}
                metadata={{ ...metadata, type: 'ENTITY' }}
                state={state}
                readOnly={readOnly}
              />
            );
          }}
        </Formik>
      </Card>
    </div>
  );
};

const defaultComponents = componentsProp => {
  return { ...getComponents(), ...componentsProp };
};

const useFormikProps = ({ contextParams, dispatch, metadata, state }) => {
  const currentForm = useCurrentForm(metadata);
  const schema = SchemaService.generateSchema(getComponents(currentForm));
  const initialValues = useMemo(() => {
    if (isUpdate(metadata)) {
      return state.data
        ? SchemaService.castSchema(
            { ...state.data, $tracking: contextParams?.tracking || {} },
            schema
          )
        : {};
    }
    return SchemaService.castSchema(
      { $tracking: contextParams?.tracking || {} },
      schema
    );
  }, [contextParams?.tracking, metadata, schema, state.data]);
  const handleSubmit = useHandleSubmit({
    contextParams,
    dispatch,
    metadata
  });
  return {
    key: currentForm.id,
    enableReinitialize: true,
    initialValues,
    onSubmit: handleSubmit,
    validateOnChange: false,
    validate: SchemaService.getValidator(schema)
  };
};

function getComponents() {
  const comps = EntityView.components;
  return {
    ...comps,
    AuroraTextField
  };
}

function useIsReadOnly(metadata) {
  const updateEndpoint = findEndpoint(metadata, { type: 'UPDATE' });
  return !useHasAccess(updateEndpoint && updateEndpoint.scope, 'UPDATE');
}

export default GridEntityForm;
